<div class="matching-shell">
  <section class="topbar-card">
    <div class="heading-row">
      <div class="title-block">
        <span class="title-icon">
          <lucide-icon [img]="ActivityIcon" [size]="18"></lucide-icon>
        </span>
        <div>
          <h1>REA Conciliation</h1>
          <p>Actual energy accounting and variance analysis</p>
        </div>
      </div>
      <div class="heading-filters">
        <label class="heading-filter">
          <span>Year</span>
          <select [ngModel]="selectedYear()" (ngModelChange)="onYearChange($event)">
            <option value="">All</option>
            @for (year of yearOptions(); track year) {
              <option [value]="year">{{ year }}</option>
            }
          </select>
        </label>
        <label class="heading-filter">
          <span>Month</span>
          <select [ngModel]="selectedMonth()" (ngModelChange)="onMonthChange($event)">
            <option value="">All</option>
            @for (month of monthOptions(); track month) {
              <option [value]="month">{{ monthLabel(month) }}</option>
            }
          </select>
        </label>
      </div>
    </div>
  </section>

  @if (matchingLoading()) {
    <section class="panel-card empty-state">Loading REA matching...</section>
  } @else if (!matching()) {
    <section class="panel-card empty-state">
      No matched REA data is available yet. Fetch a saved REA report to open the dashboard.
    </section>
  } @else {
    @let detail = matching()!;
    <div class="metrics-grid">
      <article class="metric-card">
        <div class="metric-head">
          <span class="metric-label">Total Scheduled</span>
          <span class="metric-glyph">
            <lucide-icon [img]="metricIcon('scheduled')" [size]="18"></lucide-icon>
          </span>
        </div>
        <strong class="metric-value">{{ formatNumber(detail.summary.total_scheduled_mwh) }} MWh</strong>
        <small>Approved application quantum for selected month</small>
      </article>
      <article class="metric-card">
        <div class="metric-head">
          <span class="metric-label">Total Actual (REA)</span>
          <span class="metric-glyph">
            <lucide-icon [img]="metricIcon('actual')" [size]="18"></lucide-icon>
          </span>
        </div>
        <strong class="metric-value">{{ formatNumber(detail.summary.total_actual_mwh) }} MWh</strong>
        <small>{{ formatPeriod(detail.report) }}</small>
      </article>
      <article class="metric-card">
        <div class="metric-head">
          <span class="metric-label">Total Deviation</span>
          <span class="metric-glyph metric-glyph-negative">
            <lucide-icon [img]="metricIcon('deviation')" [size]="18"></lucide-icon>
          </span>
        </div>
        <strong [class]="metricClass(detail.summary.total_deviation_mwh)">{{ formatNumber(detail.summary.total_deviation_mwh) }} MWh</strong>
        <small>{{ formatNumber(detail.summary.total_deviation_percent) }}%</small>
      </article>
      <article class="metric-card">
        <div class="metric-head">
          <span class="metric-label">Reconciled</span>
          <span class="metric-glyph metric-glyph-positive">
            <lucide-icon [img]="metricIcon('reconciled')" [size]="18"></lucide-icon>
          </span>
        </div>
        <strong class="metric-value metric-value-positive">{{ detail.summary.reconciled_count }}</strong>
        <small>Within {{ detail.summary.variance_threshold_percent }}% variance</small>
      </article>
      <article class="metric-card">
        <div class="metric-head">
          <span class="metric-label">High Variance Alerts</span>
          <span class="metric-glyph metric-glyph-alert">
            <lucide-icon [img]="metricIcon('alerts')" [size]="18"></lucide-icon>
          </span>
        </div>
        <strong class="metric-value metric-value-negative">{{ detail.summary.high_variance_alerts }}</strong>
        <small>&gt;5% deviation</small>
      </article>
    </div>

    <section class="panel-card d-none">
      <div class="section-head">
        <div>
          <h2>REA Conciliation Process</h2>
          <p>End-to-end energy accounting with auto credit/debit suggestion</p>
        </div>
      </div>

      <div class="process-grid">
        @for (step of processSteps(); track step.step) {
          <div class="process-step" [class.process-step-warn]="step.status === 'warning'">
            <div class="step-index">{{ step.step }}</div>
            <strong>{{ step.label }}</strong>
            <span>{{ step.subtitle }}</span>
          </div>
        }
      </div>
    </section>

    <section class="panel-card chart-card d-none">
      <div class="section-head">
        <div>
          <h2>REA Conciliation</h2>
          <p>Actual energy accounting and variance analysis</p>
        </div>
      </div>
      <div class="chart-wrap">
        @if (detail.daily_points.length === 0) {
          <div class="chart-empty-state">No REA daily points available to plot this graph.</div>
        } @else {
          <canvas #comparisonCanvas></canvas>
        }
      </div>
    </section>

    <section class="panel-card">
      <div class="section-head">
        <div>
          <h2>REA Conciliation Directory</h2>
          <p>Energy accounting records</p>
        </div>
        <button type="button" class="ghost-btn compact-btn export-btn" (click)="downloadSelectedReport()">
          <lucide-icon [img]="ArrowDownToLineIcon" [size]="14"></lucide-icon>
          <span>EXPORT REPORT</span>
        </button>
      </div>

      @if (detail.rows.length === 0) {
        <div class="empty-state">No approved contracts are available to show in this directory.</div>
      } @else {
        <div class="table-wrap">
          <div class="table-responsive" style="max-height: 500px; overflow-y: auto;">
            <table class="table align-middle mb-0">
              <thead class="sticky-top">
                <tr>
                  <th>S.No.</th>
                  <th>REA ID</th>
                  <th>REA</th>
                  <th>Contract / OA</th>
                  <th>Scheduled</th>
                  <th>Actual (REA)</th>
                  <th>Deviation</th>
                  <th>Provisional Bill</th>
                  <th>Final Bill</th>
                  <th>Adjustment</th>
                  <th>Suggested Note</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                @for (row of pagedRows(); track row.contract_id; let index = $index) {
                  <tr>
                    <td>{{ (currentPage() - 1) * pageSize() + index + 1 }}</td>
                    <td>{{ detail.report.report_type }} {{ detail.report.report_year }}-{{ detail.report.report_month || 'NA' }}-{{ (currentPage() - 1) * pageSize() + index + 1 }}</td>
                    <td>
                      <div class="rea-applied-value">{{ detail.report.rpc_region || 'NA' }}</div>
                    </td>
                    <td>
                      <div class="table-primary">{{ row.contract_number }}</div>
                      <small>{{ row.loa_numbers.join(', ') || 'NA' }}</small>
                    </td>
                    <td>{{ formatNumber(row.scheduled_mwh) }} MWh</td>
                    <td><strong>{{ formatNumber(row.actual_mwh) }} MWh</strong></td>
                    <td>
                      <div [class]="row.deviation_mwh >= 0 ? 'delta-positive' : 'delta-negative'">{{ formatNumber(row.deviation_mwh) }} MWh</div>
                      <small [class]="row.deviation_percent >= 0 ? 'delta-positive' : 'delta-negative'">({{ formatNumber(row.deviation_percent) }}%)</small>
                    </td>
                    <td>{{ formatCurrency(row.provisional_bill) }}</td>
                    <td><strong>{{ formatCurrency(row.final_bill) }}</strong></td>
                    <td>
                      <span [class]="row.adjustment >= 0 ? 'delta-positive' : 'delta-negative'">{{ formatCurrency(row.adjustment) }}</span>
                    </td>
                    <td>
                      @if (row.adjustment === 0) {
                        <span [class]="suggestedNoteClass(row)">{{ suggestedNoteLabel(row) }}</span>
                      } @else {
                        <button type="button" [class]="suggestedNoteClass(row)" (click)="openSuggestedNote(row)">
                          {{ suggestedNoteLabel(row) }}
                        </button>
                      }
                    </td>
                    <td><span [class]="statusClass(row)">{{ row.status }}</span></td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
        <div class="pager-row">
          <div class="pager-meta">{{ startRow() }}-{{ endRow() }} of {{ totalRows() }} records</div>
          <div class="pagination-shell">
            <button class="pager-btn" [disabled]="currentPage() <= 1" (click)="page.set(1)" aria-label="First page">
              <lucide-icon [img]="ChevronsLeftIcon" [size]="14"></lucide-icon>
            </button>
            <button class="pager-btn" [disabled]="currentPage() <= 1" (click)="page.set(currentPage() - 1)" aria-label="Previous page">
              <lucide-icon [img]="ChevronLeftIcon" [size]="14"></lucide-icon>
            </button>
            <span class="page-indicator current">{{ currentPage() }}</span>
            <button class="pager-btn" [disabled]="currentPage() >= totalPages()" (click)="page.set(currentPage() + 1)" aria-label="Next page">
              <lucide-icon [img]="ChevronRightIcon" [size]="14"></lucide-icon>
            </button>
            <button class="pager-btn" [disabled]="currentPage() >= totalPages()" (click)="page.set(totalPages())" aria-label="Last page">
              <lucide-icon [img]="ChevronsRightIcon" [size]="14"></lucide-icon>
            </button>
            <select class="pager-size-select" [ngModel]="pageSize()" (ngModelChange)="onPageSizeChange(($event || '10').toString())" aria-label="Rows per page">
              @for (size of pageSizes; track size) {
                <option [value]="size">{{ size }}/Page</option>
              }
            </select>
          </div>
        </div>
      }
    </section>
  }
</div>
